/**
 * Audit Wizard — orchestrates the 4-step submission flow.
 *
 * Routes:
 *   /sales/audits/new          → fresh wizard, creates draft on first save
 *   /sales/audits/new?draft=X  → resume existing draft
 *
 * State:
 *   draftId       — UUID, persisted to URL once created
 *   step          — 1..4 (Step 4 only when parking_program === 'myvip')
 *   formData      — Step 1 fields + cached previews
 *   files         — list from server
 *
 * Auto-save on every step transition.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import '../components/audit/audit.css';
import Step1Property from '../components/audit/Step1Property';
import Step2RentRoll from '../components/audit/Step2RentRoll';
import Step3Vehicles from '../components/audit/Step3Vehicles';
import Step4MyVIP from '../components/audit/Step4MyVIP';

export default function AuditWizardPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [draftId, setDraftId] = useState(params.get('draft') || null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(!!params.get('draft'));
  const [error, setError] = useState('');

  // When ensureDraft creates a new draft mid-session it calls setParams to
  // sticky the draft ID into the URL. That URL change re-fires this load
  // effect, which then re-fetches the (freshly empty) draft and clobbers
  // whatever the user just typed in Step 1 — including parking_program,
  // which silently kills the MyVIP step. The ref tells the load effect:
  // "we just made this one — skip the round-trip." (Real refreshes still
  // load via the initial mount path.)
  const justCreatedRef = useRef(false);

  // ─── Load existing draft on mount ──────────────────────
  useEffect(() => {
    const id = params.get('draft');
    if (!id) return;
    if (justCreatedRef.current) {
      justCreatedRef.current = false;
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await api.getAuditDraft(id);
        setDraftId(id);
        setFormData(r.draft.form_data || {});
        setStep(r.draft.current_step || 1);
        setFiles(r.files || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [params]);

  const ensureDraft = useCallback(async () => {
    if (draftId) return draftId;
    const r = await api.createAuditDraft();
    setDraftId(r.draft_id);
    justCreatedRef.current = true;
    setParams({ draft: r.draft_id });
    return r.draft_id;
  }, [draftId, setParams]);

  const refreshFiles = useCallback(async () => {
    if (!draftId) return;
    const r = await api.getAuditDraft(draftId);
    setFiles(r.files || []);
  }, [draftId]);

  const saveProgress = useCallback(async (nextFormData, nextStep) => {
    const id = await ensureDraft();
    await api.patchAuditDraft(id, { form_data: nextFormData, current_step: nextStep });
  }, [ensureDraft]);

  const onChangeForm = (next) => setFormData(next);

  const goNext = async () => {
    setError('');
    try {
      const next = step + 1;
      await saveProgress(formData, next);
      setStep(next);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e.message || 'Could not advance to the next step');
    }
  };

  const goBack = async () => {
    setError('');
    try {
      const prev = Math.max(1, step - 1);
      await saveProgress(formData, prev);
      setStep(prev);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e.message || 'Could not go back');
    }
  };

  // After Step 3, MyVIP is conditional
  const hasMyVIPStep = formData.parking_program === 'myvip';

  // Step 3 "next" button advances to step 4 if MyVIP, else submits directly
  const step3Next = async () => {
    if (hasMyVIPStep) {
      await goNext();
    } else {
      await onSubmit();
    }
  };

  const onSubmit = async () => {
    setError('');
    try {
      const id = await ensureDraft();
      // Persist final form_data first
      await api.patchAuditDraft(id, { form_data: formData });
      const r = await api.submitAuditDraft(id);
      navigate(`/sales/audits/${r.audit_id}`);
    } catch (e) {
      // Without this, server validation rejections (e.g. missing
      // parking_program) used to silently sink the click — Matt saw
      // "Next is broke". Now the error surfaces in the wizard header.
      setError(e.message || 'Could not submit audit');
    }
  };

  const saveAndExit = async () => {
    setError('');
    try {
      if (draftId) {
        await api.patchAuditDraft(draftId, { form_data: formData, current_step: step });
      }
      navigate('/sales/audits');
    } catch (e) {
      setError(e.message || 'Could not save draft');
    }
  };

  const stepCount = hasMyVIPStep ? 4 : 3;
  const stepLabels = useMemo(() => {
    const base = ['Property', 'Rent Roll', 'Vehicles'];
    if (hasMyVIPStep) base.push('MyVIP');
    return base;
  }, [hasMyVIPStep]);

  if (loading) {
    return <div className="page audit-wizard"><div className="audit-loading">Loading draft…</div></div>;
  }

  return (
    <div className="page audit-wizard">
      <header className="audit-wizard-header">
        <h1>New Parking Audit</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {draftId && (
            <button type="button" className="audit-save-exit" onClick={saveAndExit}>
              Save and Continue Later
            </button>
          )}
          {/* History entry point — moved here May 20 Phase 2.1 when the
              standalone 'Audits' sidebar link was retired. */}
          <button type="button" className="audit-save-exit" onClick={() => navigate('/sales/audits')}>
            History
          </button>
        </div>
      </header>

      {/* Progress indicator */}
      <ol className="audit-progress">
        {stepLabels.map((label, i) => {
          const idx = i + 1;
          return (
            <li
              key={label}
              className={`${idx === step ? 'active' : ''} ${idx < step ? 'done' : ''}`}
              aria-current={idx === step ? 'step' : undefined}
            >
              <span className="audit-progress-dot">{idx}</span>
              <span className="audit-progress-label">{label}</span>
            </li>
          );
        })}
      </ol>

      {error && <div className="audit-error">{error}</div>}

      {step === 1 && (
        <Step1Property
          formData={formData}
          onChange={onChangeForm}
          onNext={goNext}
        />
      )}
      {step === 2 && draftId && (
        <Step2RentRoll
          draftId={draftId}
          formData={formData}
          onChange={onChangeForm}
          onNext={goNext}
          onBack={goBack}
          files={files}
          refreshFiles={refreshFiles}
        />
      )}
      {step === 3 && draftId && (
        <Step3Vehicles
          draftId={draftId}
          formData={formData}
          onChange={onChangeForm}
          onNext={step3Next}
          onBack={goBack}
          files={files}
          refreshFiles={refreshFiles}
        />
      )}
      {step === 4 && draftId && hasMyVIPStep && (
        <Step4MyVIP
          draftId={draftId}
          formData={formData}
          onChange={onChangeForm}
          onSubmit={onSubmit}
          onBack={goBack}
          files={files}
          refreshFiles={refreshFiles}
        />
      )}
    </div>
  );
}
